import { useState } from "react";
import type { DaySummary } from "../lib/api";
import { calorieStatus, proteinLevel, type CalorieStatus, type ProteinLevel } from "../lib/goals";
import { addMonths, DOW_LABELS, formatDateKey, getMonthGrid } from "../lib/monthGrid";

type Metric = "calories" | "protein";

const PROTEIN_LEVEL_LABEL: Record<ProteinLevel, string> = {
  0: "no data",
  1: "under half",
  2: "under goal",
  3: "goal met",
};

const CAL_STATUS_LABEL: Record<CalorieStatus, string> = {
  none: "no data",
  ok: "under",
  warn: "near limit",
  over: "over",
};

type Props = {
  days: DaySummary[];
  month: Date;
  onMonthChange: (d: Date) => void;
  minMonth: Date;
  goal: { calories: number; protein_g: number };
};

export function MonthHeatmap({ days, month, onMonthChange, minMonth, goal }: Props) {
  const [metric, setMetric] = useState<Metric>("calories");

  const map = new Map(days.map((d) => [d.day, d]));
  const { year, mo, daysInMonth, firstDow, todayKey, atMin, atMax, monthLabel } = getMonthGrid(
    month,
    minMonth,
  );

  return (
    <div className="heatmap">
      <div className="heatmap-nav">
        <div className="heatmap-nav__left">
          <button
            className="ghost"
            onClick={() => onMonthChange(addMonths(month, -1))}
            disabled={atMin}
            aria-label="Previous month"
          >
            ‹
          </button>
          <span className="heatmap-nav__month">{monthLabel}</span>
          <button
            className="ghost"
            onClick={() => onMonthChange(addMonths(month, 1))}
            disabled={atMax}
            aria-label="Next month"
          >
            ›
          </button>
        </div>
        <div className="heatmap-toggle">
          <button
            className={`heatmap-toggle__btn${metric === "calories" ? " heatmap-toggle__btn--active" : ""}`}
            onClick={() => setMetric("calories")}
          >
            Cal
          </button>
          <button
            className={`heatmap-toggle__btn${metric === "protein" ? " heatmap-toggle__btn--active" : ""}`}
            onClick={() => setMetric("protein")}
          >
            Protein
          </button>
        </div>
      </div>
      <div className="heatmap-dow-row">
        {DOW_LABELS.map((l, i) => (
          <div key={i} className="heatmap-dow">
            {l}
          </div>
        ))}
      </div>
      <div className="heatmap-grid">
        {Array.from({ length: firstDow }).map((_, i) => (
          <div key={`f${i}`} className="heatmap-cell heatmap-cell--filler" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const key = formatDateKey(year, mo, day);
          const entry = map.get(key);
          const isToday = key === todayKey;
          const todayClass = isToday ? " heatmap-cell--today" : "";

          let metricClass: string;
          let tooltipVal: string;
          if (metric === "protein") {
            const level = proteinLevel(entry?.protein_g ?? 0, goal.protein_g);
            metricClass = ` heatmap-cell--protein heatmap-cell--l${level}`;
            tooltipVal = entry
              ? `${entry.protein_g}g protein (${PROTEIN_LEVEL_LABEL[level]})`
              : "no data";
          } else {
            const status = calorieStatus(entry?.calories ?? 0, goal.calories);
            metricClass = status === "none" ? " heatmap-cell--l0" : ` heatmap-cell--cal-${status}`;
            tooltipVal = entry
              ? `${Math.round(entry.calories)} kcal (${CAL_STATUS_LABEL[status]})`
              : "no data";
          }

          return (
            <div
              key={key}
              className={`heatmap-cell${metricClass}${todayClass}`}
              title={`${key}: ${tooltipVal}`}
            />
          );
        })}
      </div>
      <div className="legend">
        {metric === "calories" ? (
          <>
            <span className="legend-item">
              <span className="legend-swatch legend-swatch--cal-ok" /> Under goal
            </span>
            <span className="legend-item">
              <span className="legend-swatch legend-swatch--cal-warn" /> Near limit
            </span>
            <span className="legend-item">
              <span className="legend-swatch legend-swatch--cal-over" /> Over goal
            </span>
          </>
        ) : (
          <>
            <span className="legend-item">
              <span className="legend-swatch legend-swatch--pro-l1" /> Under half
            </span>
            <span className="legend-item">
              <span className="legend-swatch legend-swatch--pro-l2" /> Under goal
            </span>
            <span className="legend-item">
              <span className="legend-swatch legend-swatch--pro-l3" /> Goal met
            </span>
          </>
        )}
      </div>
    </div>
  );
}
